import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, Headset, Maximize2, Minimize2, Minus, Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { useSupportApi } from '../../../common/hooks/use-support-api.hook';
import type { ConversationCategory, SupportAttachment, SupportMessage, VendorConversation } from '../../../common/models/support.model';
import { onRealtime } from '../../../common/services/realtime.service';
import { captureContext, CONVERSATION_STATUS_META } from '../support-meta';
import { useSupportWidget, type WidgetView } from './support-widget.store';
import { SupportHome } from './SupportHome';
import { NewConversation } from './NewConversation';
import { SupportThread } from './SupportThread';
import { ConversationList } from './ConversationList';

/** Fired to open the widget from outside React trees that can't reach the store. */
export const OPEN_SUPPORT_EVENT = 'ardealy:open-support';

// The path says where the vendor was; the tab title is the same on every page.
const pageLabel = () => window.location.pathname.slice(0, 120);

/**
 * The vendor's line to Ardealy: a floating chat that opens from the top bar,
 * minimises to a pill, and expands into a two-pane inbox. Live over the app's
 * socket; replies appear at once and are confirmed or rolled back.
 */
/** The floating button's offset and height plus a gap, less the corner's usual 24px: what its other occupants rise by. */
export const FAB_CLEARANCE_VAR = '--support-fab-clearance';
const FAB_CLEARANCE = '4.25rem';

export function SupportWidget() {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const api = useSupportApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [params, setParams] = useSearchParams();

  const {
    mode, expanded, view, conversations, loaded, messages, unread, drafts,
    open, minimize, close, toggleExpanded, setView, setConversations, upsertConversation,
    setMessages, addMessage, removeMessage, setUnread, markConversationRead, setDraft,
  } = useSupportWidget();

  // Signing out unmounts the vendor shell: forget this vendor's conversations
  // so the next account signed in on this tab starts clean.
  useEffect(() => () => useSupportWidget.getState().reset(), []);

  // The button always sits in the bottom-right corner; toasts and the other
  // corner panels read this to stay clear of it.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(FAB_CLEARANCE_VAR, FAB_CLEARANCE);
    return () => {
      root.style.removeProperty(FAB_CLEARANCE_VAR);
    };
  }, []);

  // ── Data ───────────────────────────────────────────────────────────────

  useEffect(() => {
    apiRef.current.getUnread().then((r) => r && setUnread(r.count)).catch(() => undefined);
    const offs = [
      onRealtime('support:conversation', (conv: VendorConversation) => {
        if (!conv?._id) return;
        if (useSupportWidget.getState().loaded) upsertConversation(conv);
        else apiRef.current.getUnread().then((r) => r && setUnread(r.count)).catch(() => undefined);
      }),
      onRealtime('support:message', ({ conversationId, message }: { conversationId: string; message: SupportMessage }) => {
        if (conversationId && message) addMessage(conversationId, message);
      }),
      onRealtime('support:read', ({ conversationId }: { conversationId: string }) => markConversationRead(conversationId)),
    ];
    return () => offs.forEach((off) => off());
  }, [setUnread, upsertConversation, addMessage, markConversationRead]);

  useEffect(() => {
    if (mode !== 'open' || loaded) return;
    apiRef.current.listConversations().then((list) => list && setConversations(list)).catch(() => setConversations([]));
  }, [mode, loaded, setConversations]);

  const threadId = view.kind === 'thread' ? view.id : undefined;
  const thread = conversations.find((c) => c._id === threadId);

  useEffect(() => {
    if (!threadId || messages[threadId]) return;
    apiRef.current
      .getConversation(threadId)
      .then((r) => {
        if (!r) return;
        setMessages(threadId, r.messages);
        upsertConversation(r.conversation);
      })
      .catch(() => setView({ kind: 'home' }));
  }, [threadId, messages, setMessages, upsertConversation, setView]);

  // Reading a thread clears its unread, while the widget is actually showing it.
  useEffect(() => {
    if (mode !== 'open' || !threadId || !thread?.unread || document.visibilityState !== 'visible') return;
    markConversationRead(threadId);
    apiRef.current.markRead(threadId).catch(() => undefined);
  }, [mode, threadId, thread?.unread, markConversationRead]);

  // ── Ways in: ?support=<id|new> (emails, notifications), and an event ──

  useEffect(() => {
    const target = params.get('support');
    if (!target) return;
    open(target === 'new' ? { kind: 'new' } : { kind: 'thread', id: target });
    params.delete('support');
    setParams(params, { replace: true });
  }, [params, setParams, open]);

  useEffect(() => {
    const onOpen = (e: Event) => open((e as CustomEvent<WidgetView | undefined>).detail);
    window.addEventListener(OPEN_SUPPORT_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_SUPPORT_EVENT, onOpen);
  }, [open]);

  // ── Actions ────────────────────────────────────────────────────────────

  const upload = useCallback((file: File, onProgress: (f: number) => void) => apiRef.current.upload(file, onProgress), []);

  const createConversation = async (p: { category: ConversationCategory; subject: string; body: string; attachments: SupportAttachment[] }) => {
    const res = await apiRef.current.createConversation({ ...p, context: captureContext(pageLabel()) });
    if (!res) throw new Error(t('SUPPORT.COMPOSER.SEND_FAILED'));
    upsertConversation(res.conversation);
    setMessages(res.conversation._id, [res.message]);
    setDraft('new', '');
    setView({ kind: 'thread', id: res.conversation._id });
  };

  const reply = async (id: string, p: { body: string; attachments: SupportAttachment[] }) => {
    const tempId = `pending-${Date.now()}`;
    addMessage(id, {
      _id: tempId,
      conversation: id,
      authorKind: 'vendor',
      author: { email: user?.email, name: [user?.firstname, user?.lastname].filter(Boolean).join(' ') || undefined },
      body: p.body,
      attachments: p.attachments,
      createdAt: new Date().toISOString(),
      pending: true,
    });
    try {
      const saved = await apiRef.current.sendMessage(id, p);
      if (saved) addMessage(id, saved, tempId);
    } catch (e) {
      removeMessage(id, tempId);
      throw e;
    }
  };

  // ── Chrome ─────────────────────────────────────────────────────────────

  // Closed or minimised, the chat is one click away on every page.
  if (mode !== 'open') {
    return (
      <button
        type="button"
        onClick={() => open()}
        aria-label={unread > 0 ? t('SUPPORT.WIDGET.LAUNCHER_UNREAD', { count: unread }) : t('SUPPORT.WIDGET.LAUNCHER')}
        title={t('SUPPORT.WIDGET.LAUNCHER')}
        className="fixed bottom-8 end-8 z-[45] flex size-12 [body[data-hide-support-fab]_&]:hidden items-center justify-center rounded-full border bg-background text-foreground shadow-lg transition-transform animate-in fade-in-0 zoom-in-95 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="support-launcher"
      >
        <Headset className="size-5" />
        {unread > 0 && (
          <span className="absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-[11px] font-medium tabular-nums text-background">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
    );
  }

  const title =
    view.kind === 'thread' ? thread?.subject || t('SUPPORT.WIDGET.TITLE')
      : view.kind === 'new' ? t('SUPPORT.NEW.TITLE')
        : t('SUPPORT.WIDGET.TITLE');
  const status = thread ? CONVERSATION_STATUS_META[thread.status] : null;
  const showBack = view.kind !== 'home' && !expanded;

  const body =
    view.kind === 'thread' ? (
      <SupportThread
        key={view.id}
        conversation={thread}
        messages={messages[view.id]}
        upload={upload}
        onSend={(p) => reply(view.id, p)}
        draft={drafts[view.id]}
        onDraftChange={(text) => setDraft(view.id, text)}
      />
    ) : view.kind === 'new' ? (
      <NewConversation
        key={`new-${view.category || ''}`}
        initialCategory={view.category}
        upload={upload}
        onSubmit={createConversation}
        pageLabel={pageLabel()}
        draft={drafts.new}
        onDraftChange={(text) => setDraft('new', text)}
      />
    ) : (
      <SupportHome
        firstName={user?.firstname}
        conversations={conversations}
        loaded={loaded}
        showList={!expanded}
        onNew={(category) => setView({ kind: 'new', category })}
        onOpen={(id) => setView({ kind: 'thread', id })}
      />
    );

  return (
    <section
      role="dialog"
      aria-label={t('SUPPORT.WIDGET.TITLE')}
      className={cn(
        'fixed z-[45] flex flex-col overflow-hidden border bg-background shadow-xl animate-in fade-in-0 slide-in-from-bottom-4 duration-200',
        'inset-0 sm:inset-auto sm:bottom-4 sm:end-4 sm:rounded-2xl',
        expanded
          ? 'sm:h-[min(760px,calc(100vh-2rem))] sm:w-[min(980px,calc(100vw-2rem))]'
          : 'sm:h-[min(640px,calc(100vh-2rem))] sm:w-[400px]',
      )}
      data-testid="support-widget"
    >
      <header className="flex h-13 shrink-0 items-center gap-2 border-b px-3">
        {showBack ? (
          <Button variant="ghost" size="icon-sm" onClick={() => setView({ kind: 'home' })} aria-label={t('SUPPORT.WIDGET.BACK')}>
            <ArrowLeft className="rtl:rotate-180" />
          </Button>
        ) : (
          <span className="flex size-8 items-center justify-center rounded-lg bg-foreground text-background">
            <Headset className="size-4" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{title}</p>
          <p className="truncate text-[11px] text-muted-foreground">
            {status ? (
              <span className="inline-flex items-center gap-1">
                <span className={cn('size-1.5 rounded-full', status.dot)} />
                {t(status.vendorLabelKey)}
              </span>
            ) : (
              t('SUPPORT.WIDGET.SUBTITLE')
            )}
          </p>
        </div>
        <Button variant="ghost" size="icon-sm" className="hidden sm:inline-flex" onClick={toggleExpanded} aria-label={expanded ? t('SUPPORT.WIDGET.COLLAPSE') : t('SUPPORT.WIDGET.EXPAND')}>
          {expanded ? <Minimize2 /> : <Maximize2 />}
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={minimize} aria-label={t('SUPPORT.WIDGET.MINIMIZE')}>
          <Minus />
        </Button>
        <Button variant="ghost" size="icon-sm" onClick={close} aria-label={t('SUPPORT.WIDGET.CLOSE')}>
          <X />
        </Button>
      </header>

      {expanded ? (
        <div className="flex min-h-0 flex-1">
          <aside className="flex w-72 shrink-0 flex-col border-e">
            <div className="p-3">
              <Button variant="outline" className="w-full" onClick={() => setView({ kind: 'new' })}>
                <Plus data-icon="inline-start" />
                {t('SUPPORT.HOME.NEW')}
              </Button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
              {conversations.length ? (
                <ConversationList conversations={conversations} activeId={threadId} onOpen={(id) => setView({ kind: 'thread', id })} />
              ) : (
                <p className="px-3 py-4 text-sm text-muted-foreground">{loaded ? t('SUPPORT.HOME.NONE_YET') : ''}</p>
              )}
            </div>
          </aside>
          <div className="flex min-w-0 flex-1 flex-col">{body}</div>
        </div>
      ) : (
        body
      )}
    </section>
  );
}

/** Top-bar button: the same chat as the floating one, with the count of unread replies. */
export function SupportLauncher() {
  const { t } = useTranslation();
  const unread = useSupportWidget((s) => s.unread);
  const mode = useSupportWidget((s) => s.mode);
  const { open, minimize } = useSupportWidget.getState();
  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative"
      onClick={() => (mode === 'open' ? minimize() : open())}
      aria-label={t('SUPPORT.WIDGET.LAUNCHER')}
      title={t('SUPPORT.WIDGET.LAUNCHER')}
      data-testid="support-topbar-launcher"
    >
      <Headset />
      {unread > 0 && (
        <span className="absolute -end-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-medium tabular-nums text-background">
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Button>
  );
}
